import type { SnapshotComparisonResult } from '../types/snapshotComparison';
import { fetchSnapshotComparison } from '../services/SnapshotComparison/fetchSnapshotComparisonService';

// ── Live mock constants (hardcoded known-good snapshot paths) ──────────────
const LIVE_MOCK_MIGRATION_ID = 'd08d06c5-5e7e-470d-b2ed-314e9bf6d79e';
const LIVE_MOCK_SNAPSHOT1 = 'd08d06c5-5e7e-470d-b2ed-314e9bf6d79e/source/2026_02_26_20_19_39';
const LIVE_MOCK_SNAPSHOT2 = 'd08d06c5-5e7e-470d-b2ed-314e9bf6d79e/sourceOrgExport/2026_03_30_07_03_12';

/**
 * Wrapper for live mock data - calls the real API with hardcoded known-good snapshot paths.
 * MOCK FUNCTION: Delete this when switching to real API.
 * Used when USE_LIVE_MOCK is true in SnapshotComparisonToolPage.
 */
export const liveMockFetchSnapshotComparison = (
  bearerToken: string
): Promise<SnapshotComparisonResult> => {
  return fetchSnapshotComparison(
    bearerToken,
    LIVE_MOCK_MIGRATION_ID,
    LIVE_MOCK_SNAPSHOT1,
    LIVE_MOCK_SNAPSHOT2
  );
};

/**
 * Returns realistic mock snapshot comparison data for a given migration.
 * MOCK FUNCTION: Delete this when switching to real API.
 * Used when USE_MOCK_DATA is true in SnapshotComparisonToolPage.
 * Structure matches the GET /api/Migrations/{id}/compare response shape.
 */
export const mockFetchSnapshotComparison = (
  migrationId: string
): Promise<SnapshotComparisonResult> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        migrationId,
        snapshot1Path: `${migrationId}/targetOrgSnapshot-preImport`,
        snapshot2Path: `${migrationId}/targetOrgSnapshot-postImport`,
        comparedAtUtc: new Date().toISOString(),
        summary: {
          totalResourceTypes: 3,
          resourceTypesChanged: 3,
          instancesAdded: 2,
          instancesDeleted: 1,
          instancesModified: 3,
          instancesUnchanged: 12,
          propertiesChanged: 5,
          changesIgnored: 0,
        },
        resourceTypes: [
          {
            resourceType: 'genesyscloud_routing_queue',
            fileName: 'genesyscloud_routing_queue.tf.json',
            fileStatus: 'Modified',
            summary: { added: 2, deleted: 1, modified: 3, unchanged: 0 },
            instances: [
              {
                label: 'Support_Queue_Main',
                displayName: 'Support Queue Main',
                status: 'Added',
                jsonPath: '/resource/genesyscloud_routing_queue/Support_Queue_Main',
                properties: [],
                beforeState: null,
                afterState: { name: 'Support Queue Main' },
              },
              {
                label: 'Sales_Queue_Priority',
                displayName: 'Sales Queue Priority',
                status: 'Added',
                jsonPath: '/resource/genesyscloud_routing_queue/Sales_Queue_Priority',
                properties: [],
                beforeState: null,
                afterState: { name: 'Sales Queue Priority' },
              },
              {
                label: 'Billing_Queue',
                displayName: 'Billing Queue',
                status: 'Modified',
                jsonPath: '/resource/genesyscloud_routing_queue/Billing_Queue',
                properties: [
                  {
                    jsonPath: '/resource/genesyscloud_routing_queue/Billing_Queue/acwSettings',
                    propertyName: 'acwSettings',
                    changeType: 'Modified',
                    beforeValue: null,
                    afterValue: null,
                    isIgnored: false,
                    ignoreReason: null,
                    children: [
                      {
                        jsonPath: '/resource/genesyscloud_routing_queue/Billing_Queue/acwSettings/timeoutMs',
                        propertyName: 'timeoutMs',
                        changeType: 'Modified',
                        beforeValue: 30000,
                        afterValue: 60000,
                        isIgnored: false,
                        ignoreReason: null,
                        children: null,
                      },
                    ],
                  },
                ],
                beforeState: null,
                afterState: null,
              },
              {
                label: 'Tech_Support_Queue',
                displayName: 'Tech Support Queue',
                status: 'Modified',
                jsonPath: '/resource/genesyscloud_routing_queue/Tech_Support_Queue',
                properties: [
                  {
                    jsonPath: '/resource/genesyscloud_routing_queue/Tech_Support_Queue/name',
                    propertyName: 'name',
                    changeType: 'Modified',
                    beforeValue: null,
                    afterValue: null,
                    isIgnored: false,
                    ignoreReason: null,
                    children: [
                      {
                        jsonPath: '/resource/genesyscloud_routing_queue/Tech_Support_Queue/name',
                        propertyName: 'name',
                        changeType: 'Modified',
                        beforeValue: 'Technical_Support',
                        afterValue: 'Tech_Support_Queue',
                        isIgnored: false,
                        ignoreReason: null,
                        children: null,
                      },
                    ],
                  },
                ],
                beforeState: null,
                afterState: null,
              },
              {
                label: 'Deprecated_Queue',
                displayName: 'Deprecated Queue',
                status: 'Deleted',
                jsonPath: '/resource/genesyscloud_routing_queue/Deprecated_Queue',
                properties: [],
                beforeState: { name: 'Deprecated Queue' },
                afterState: null,
              },
            ],
          },
          {
            resourceType: 'genesyscloud_group',
            fileName: 'genesyscloud_group.tf.json',
            fileStatus: 'Modified',
            summary: { added: 0, deleted: 0, modified: 1, unchanged: 5 },
            instances: [
              {
                label: 'Billing_Admins',
                displayName: 'Billing Admins',
                status: 'Modified',
                jsonPath: '/resource/genesyscloud_group/Billing_Admins',
                properties: [
                  {
                    jsonPath: '/resource/genesyscloud_group/Billing_Admins/description',
                    propertyName: 'description',
                    changeType: 'Modified',
                    beforeValue: null,
                    afterValue: null,
                    isIgnored: false,
                    ignoreReason: null,
                    children: [
                      {
                        jsonPath: '/resource/genesyscloud_group/Billing_Admins/description',
                        propertyName: 'description',
                        changeType: 'Modified',
                        beforeValue: 'Billing department',
                        afterValue: 'Billing & Finance',
                        isIgnored: false,
                        ignoreReason: null,
                        children: null,
                      },
                    ],
                  },
                ],
                beforeState: null,
                afterState: null,
              },
            ],
          },
          {
            resourceType: 'genesyscloud_user',
            fileName: 'genesyscloud_user.tf.json',
            fileStatus: 'Modified',
            summary: { added: 0, deleted: 0, modified: 1, unchanged: 7 },
            instances: [
              {
                label: 'david_brown_example_com',
                displayName: 'David Brown',
                status: 'Modified',
                jsonPath: '/resource/genesyscloud_user/david_brown_example_com',
                properties: [
                  {
                    jsonPath: '/resource/genesyscloud_user/david_brown_example_com/department',
                    propertyName: 'department',
                    changeType: 'Modified',
                    beforeValue: null,
                    afterValue: null,
                    isIgnored: false,
                    ignoreReason: null,
                    children: [
                      {
                        jsonPath: '/resource/genesyscloud_user/david_brown_example_com/department',
                        propertyName: 'department',
                        changeType: 'Modified',
                        beforeValue: 'Support',
                        afterValue: 'Technical Support',
                        isIgnored: false,
                        ignoreReason: null,
                        children: null,
                      },
                    ],
                  },
                ],
                beforeState: null,
                afterState: null,
              },
            ],
          },
        ],
        deletedFiles: [],
        addedFiles: [],
        errors: [],
      });
    }, 600);
  });
};
